"use client";

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { useAuthStore } from "@/store/auth";

interface TrendingPoint {
  date: string;
  value: number;
}
interface TrendingSeries {
  manufacturer_id: number;
  company: string;
  categories: string[];
  total: number;
  points: TrendingPoint[];
}
interface TrendingOut {
  window_days: number;
  click_data_ready: boolean;
  click_threshold: number;
  click_total: number;
  click_top: TrendingSeries[];
  datalab_top: TrendingSeries[];
  fallback_used: boolean;
}

interface ManufacturerOpt {
  id: number;
  company: string;
  categories: string[];
}

const COLORS = [
  "#FF5C35", "#60A5FA", "#4ADE80", "#FACC15", "#A78BFA",
  "#F472B6", "#22D3EE", "#FB923C", "#94A3B8", "#34D399",
];

function buildChartData(series: TrendingSeries[]): { date: string; [key: string]: number | string }[] {
  const dateSet = new Set<string>();
  series.forEach((s) => s.points.forEach((p) => dateSet.add(p.date)));
  const dates = Array.from(dateSet).sort();
  return dates.map((date) => {
    const row: { date: string; [k: string]: number | string } = { date: date.slice(5) };
    series.forEach((s) => {
      const p = s.points.find((pt) => pt.date === date);
      row[s.company] = p ? p.value : 0;
    });
    return row;
  });
}

const MAX_SELECTED = 5;          // 차트 가독성 — 라인 5개 한도
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_MIN_LEN = 2;         // 2자 이상 시 서버 API 호출 (제품명 매칭)

export function TrendingSection() {
  const { token } = useAuthStore();
  const [data, setData] = useState<TrendingOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[] | null>(null);  // null = 자동 TOP 5
  const [allMfrs, setAllMfrs] = useState<ManufacturerOpt[]>([]);
  const [searchResults, setSearchResults] = useState<ManufacturerOpt[] | null>(null); // null = 검색 안 함, [] = 검색 결과 0
  const [searching, setSearching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

  // trending 데이터 로드 — manufacturer_trending = free_public (비로그인도 가능, 2026-05-11)
  useEffect(() => {
    // 명시적 빈 상태 (사용자가 전체 해제) — API 호출 skip 하고 차트만 비움
    if (selectedIds !== null && selectedIds.length === 0) {
      setData((prev) => prev
        ? { ...prev, click_top: [], datalab_top: [] }
        : { window_days: 30, click_data_ready: false, click_threshold: 30, click_total: 0, click_top: [], datalab_top: [], fallback_used: false });
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = selectedIds && selectedIds.length > 0
      ? { ids: selectedIds.join(",") }
      : {};
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get<TrendingOut>("/api/manufacturers/trending", { params, headers })
      .then((r) => setData(r.data))
      .catch(() => setError("불러오기 실패"))
      .finally(() => setLoading(false));
  }, [token, selectedIds]);

  // 전체 업체 리스트 (picker 용) — 1회 로드
  useEffect(() => {
    if (!token) return;
    axios
      .get<{ manufacturers: ManufacturerOpt[] }>("/api/manufacturers", {
        params: { size: 50, page: 1 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setAllMfrs(r.data.manufacturers))
      .catch(() => {});
  }, [token]);

  // picker 검색 — 2자 이상 시 서버 API (제품명까지 매칭). debounce.
  useEffect(() => {
    if (!token || !pickerOpen) return;
    const sq = searchTerm.trim();
    if (sq.length < SEARCH_MIN_LEN) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      axios
        .get<{ manufacturers: ManufacturerOpt[] }>("/api/manufacturers", {
          params: { q: sq, size: 50, page: 1 },
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((r) => setSearchResults(r.data.manufacturers))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, pickerOpen, token]);

  // 자동 모드 → 사용자 모드 전환 (default = datalab TOP, 사장님 관심 차트 제거됨)
  const enterUserMode = () => {
    if (!data) return;
    const idSet = new Set<number>();
    data.datalab_top.forEach((s) => idSet.add(s.manufacturer_id));
    setSelectedIds(Array.from(idSet));
  };

  const removeId = (id: number) => {
    if (selectedIds === null) {
      // 자동 모드 → 현재 차트의 ID 들로 user mode 진입 후 제거
      enterUserMode();
      // 다음 effect 에서 리렌더 후 처리 (간단히 직접 한 번 더 — UX 위해)
      setTimeout(() => setSelectedIds((prev) => (prev ?? []).filter((x) => x !== id)), 0);
      return;
    }
    setSelectedIds(selectedIds.filter((x) => x !== id));
  };

  const togglePending = (id: number) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const applySelection = (autoModeFallback: number[]) => {
    if (pendingIds.size === 0) {
      closePicker();
      return;
    }
    setSelectedIds((prev) => {
      const cur = prev ?? autoModeFallback;
      const merged = [...cur];
      pendingIds.forEach((id) => {
        if (!merged.includes(id) && merged.length < MAX_SELECTED) merged.push(id);
      });
      return merged;
    });
    closePicker();
  };

  const closePicker = () => {
    setPickerOpen(false);
    setSearchTerm("");
    setPendingIds(new Set());
    setSearchResults(null);
    setSearching(false);
  };

  const resetToAuto = () => setSelectedIds(null);

  const datalabChart = useMemo(() => buildChartData(data?.datalab_top ?? []), [data]);
  const clickChart = useMemo(() => buildChartData(data?.click_top ?? []), [data]);

  if (loading && !data) {
    return (
      <section className="card mb-3 animate-pulse">
        <div className="h-4 w-32 bg-bg-elevated rounded mb-3" />
        <div className="h-60 bg-bg-elevated rounded" />
      </section>
    );
  }
  if (error || !data) return null;

  const hasDatalab = data.datalab_top.length > 0;
  const hasClicks = data.click_data_ready && data.click_top.length > 0;
  // 빈 상태 (사용자 전체 해제) 도 컴포넌트는 살리기 — placeholder + picker 노출

  // 2026-05-04 fix: 사장님 관심 차트 제거 후 — datalab_top 만 사용 (이전엔 click_top union → 칩 6개 표시 버그)
  const activeIds = new Set<number>();
  data.datalab_top.forEach((s) => activeIds.add(s.manufacturer_id));
  const activeMfrs = Array.from(activeIds).map((id) => {
    const fromData = data.datalab_top.find((s) => s.manufacturer_id === id);
    return { id, company: fromData?.company ?? "-", categories: fromData?.categories ?? [] };
  });

  // dropdown 옵션:
  //   - 검색어 ≥ 2자 + 서버 결과 있음 → 서버 결과 (제품명 매칭 포함)
  //   - 검색어 0~1자 → 전체 업체 (클라이언트 측 substring)
  //   - 활성 업체는 모두 제외
  const sq = searchTerm.trim().toLowerCase();
  let baseOpts: ManufacturerOpt[];
  if (sq.length >= SEARCH_MIN_LEN && searchResults !== null) {
    baseOpts = searchResults;
  } else if (sq.length > 0) {
    // 1자 검색은 클라이언트 측 substring (서버 q_key < 2 가드 우회 — picker UX)
    baseOpts = allMfrs.filter((m) => m.company.toLowerCase().includes(sq));
  } else {
    baseOpts = allMfrs;
  }
  const filteredOpts = baseOpts.filter((m) => !activeIds.has(m.id));
  const remainingSlots = MAX_SELECTED - activeMfrs.length;

  return (
    <section className="card mb-3 space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-bold text-text">공급처별 네이버 검색빈도 조회</h2>
        <div className="flex items-center gap-2">
          {activeMfrs.length > 0 && (
            <button
              onClick={() => setSelectedIds([])}
              className="text-2xs text-text-sub hover:text-red-400"
            >
              ✕ 전체 해제
            </button>
          )}
          {selectedIds !== null && (
            <button onClick={resetToAuto} className="text-2xs text-text-sub hover:text-text">
              ↺ 자동 TOP 5
            </button>
          )}
        </div>
      </div>

      {/* 업체 선택 칩 */}
      <div className="flex flex-wrap gap-1.5 items-center">
        {activeMfrs.map((m, i) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1 text-2xs bg-bg-elevated border border-border rounded-full pl-2 pr-1 py-0.5"
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="text-text">{m.company}</span>
            <button
              onClick={() => removeId(m.id)}
              className="text-text-sub hover:text-red-400 px-1"
              aria-label="제거"
            >
              ✕
            </button>
          </span>
        ))}
        {activeMfrs.length < MAX_SELECTED && (
          <div className="relative">
            <button
              onClick={() => setPickerOpen((v) => !v)}
              className="text-2xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30 hover:bg-primary/20"
            >
              + 업체 추가
            </button>
            {pickerOpen && (
              <div className="absolute top-full left-0 mt-1 z-20 bg-bg-card border border-border rounded-lg shadow-xl w-64 max-h-80 flex flex-col">
                {/* 검색 — 2자 이상 시 제품명까지 매칭 */}
                <div className="p-2 border-b border-border space-y-1">
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="업체명·제품명 (예: 딸기, 두바이초콜릿)"
                    autoFocus
                    className="w-full text-xs bg-bg-elevated text-text px-2 py-1.5 rounded border border-border outline-none focus:border-primary/50"
                  />
                  {searching && <p className="text-2xs text-text-sub">검색 중...</p>}
                  {!searching && sq.length >= SEARCH_MIN_LEN && searchResults !== null && (
                    <p className="text-2xs text-text-sub">
                      &quot;{searchTerm}&quot; 검색 결과 {filteredOpts.length}곳
                      {searchResults.length > filteredOpts.length && ` (이미 선택된 ${searchResults.length - filteredOpts.length}곳 제외)`}
                    </p>
                  )}
                </div>
                {/* 리스트 */}
                <div className="flex-1 overflow-y-auto">
                  {filteredOpts.length === 0 ? (
                    <p className="text-2xs text-text-sub text-center py-4">
                      {sq ? `"${searchTerm}" 결과 없음` : "추가 가능한 업체 없음"}
                    </p>
                  ) : (
                    filteredOpts.map((m) => {
                      const checked = pendingIds.has(m.id);
                      const wouldExceed = !checked && pendingIds.size >= remainingSlots;
                      return (
                        <button
                          key={m.id}
                          onClick={() => !wouldExceed && togglePending(m.id)}
                          disabled={wouldExceed}
                          className={`flex items-center w-full text-left text-xs px-3 py-2 border-b border-border last:border-0 transition-colors ${
                            checked ? "bg-primary/10 text-text" : "text-text hover:bg-bg-elevated"
                          } ${wouldExceed ? "opacity-30 cursor-not-allowed" : ""}`}
                        >
                          <span className={`inline-flex items-center justify-center w-4 h-4 mr-2 rounded border ${
                            checked ? "bg-primary border-primary text-bg" : "border-border"
                          }`}>
                            {checked && "✓"}
                          </span>
                          <span className="truncate flex-1">{m.company}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                {/* 적용 footer */}
                <div className="p-2 border-t border-border flex items-center gap-2">
                  <span className="text-2xs text-text-sub flex-1">
                    {pendingIds.size > 0
                      ? `${pendingIds.size}개 선택 (남은 자리 ${remainingSlots})`
                      : `남은 자리 ${remainingSlots}개`}
                  </span>
                  <button
                    onClick={closePicker}
                    className="text-2xs text-text-sub px-2 py-1 hover:text-text"
                  >
                    취소
                  </button>
                  <button
                    onClick={() => applySelection(activeMfrs.map((m) => m.id))}
                    disabled={pendingIds.size === 0}
                    className="text-2xs px-3 py-1 rounded bg-primary text-bg font-semibold disabled:opacity-30"
                  >
                    적용
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {activeMfrs.length === 0 ? (
        <div className="rounded-lg bg-bg-elevated/50 border border-dashed border-border p-6 text-center">
          <p className="text-2xs text-text-sub leading-relaxed">
            위 [+ 업체 추가] 에서 비교하고 싶은 업체를 선택하세요.
            <br />
            또는 [↺ 자동 TOP 5] 로 인기 업체 자동 표시.
          </p>
        </div>
      ) : (
        <>
          {hasDatalab && (
            <ChartBlock
              subtitle="네이버 데이터랩 검색지수 (0~100)"
              series={data.datalab_top}
              chartData={datalabChart}
              activeMfrs={activeMfrs}
            />
          )}

          {/* 2026-05-04: "사장님 관심" 차트 제거 — 사용자 결정 (가독성 떨어짐).
              click 추적 (POST /view) + 어드민 통계용 데이터는 그대로 유지. */}
        </>
      )}
    </section>
  );
}

function ChartBlock({
  subtitle, series, chartData, activeMfrs,
}: {
  subtitle: string;
  series: TrendingSeries[];
  chartData: { date: string; [k: string]: number | string }[];
  activeMfrs: { id: number; company: string }[];
}) {
  // 색상 고정 — activeMfrs 순서 기준
  const colorByCompany: Record<string, string> = {};
  activeMfrs.forEach((m, i) => { colorByCompany[m.company] = COLORS[i % COLORS.length]; });

  return (
    <div>
      <div className="flex justify-end mb-1">
        <p className="text-2xs text-text-sub">{subtitle}</p>
      </div>
      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="2 2" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#888" }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10, fill: "#888" }} />
            <Tooltip
              contentStyle={{
                background: "#1a1a1a",
                border: "1px solid #333",
                borderRadius: 8,
                fontSize: 11,
              }}
            />
            {series.map((s) => (
              <Line
                key={s.manufacturer_id}
                type="monotone"
                dataKey={s.company}
                stroke={colorByCompany[s.company] ?? "#888"}
                strokeWidth={1.8}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
